import type { PropsWithChildren, RefObject } from "react";
import { useCallback, useEffect } from "react";

import IconButton from "@/ui/core/buttons/IconButton";
import { FAIconName } from "@/ui/core/Icon";
import Box from "@/ui/layout/Box";
import type { FlashListRef } from "@/ui/layout/list/FlatList";
import { ZIndexToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

enum HorizontalListScrollConfig {
  LayoutDelayMs = 100,
  ScrollAmountFactor = 0.75,
  // Allow a small threshold to account for minor rounding differences in scroll position calculations
  ScrollRoundingThresholdPx = 2,
}
interface HorizontalListNavigationButtonsProps<
  T = unknown,
> extends BaseViewProps {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  isListEmpty: boolean;
  listRef: RefObject<FlashListRef<T> | null>;
  onRecheckScrollState?: () => void;
}

const scrollByViewport = <T,>(params: {
  direction: "next" | "previous";
  listRef: RefObject<FlashListRef<T> | null>;
}) => {
  const { direction, listRef } = params;
  if (!listRef.current) {
    return;
  }

  const scrollNode = listRef.current.getScrollableNode();

  // Scroll by 75% of viewport width for better continuity
  const scrollAmount =
    scrollNode.clientWidth * HorizontalListScrollConfig.ScrollAmountFactor;

  const newOffset =
    direction === "next"
      ? scrollNode.scrollLeft + scrollAmount
      : Math.max(0, scrollNode.scrollLeft - scrollAmount);

  // eslint-disable-next-line @typescript-eslint/naming-convention -- FlashList API uses 'animated' not 'isAnimated'
  listRef.current.scrollToOffset({ animated: true, offset: newOffset });
};

interface FloatingIconContainerProps extends PropsWithChildren {
  side: "left" | "right";
}

const FloatingIconContainer = ({
  children,
  side,
}: FloatingIconContainerProps) => {
  return (
    <Box
      left={side === "left" ? 8 : undefined}
      position="absolute"
      right={side === "right" ? 8 : undefined}
      top="50%"
      transform={[{ translateY: -20 }]}
      zIndex={ZIndexToken.FloatingContent}
    >
      {children}
    </Box>
  );
};

const HorizontalListNavigationButtons = <T,>({
  canScrollLeft,
  canScrollRight,
  isListEmpty,
  listRef,
  onRecheckScrollState,
  testID,
}: HorizontalListNavigationButtonsProps<T>) => {
  useEffect(() => {
    if (!isWeb() || !onRecheckScrollState) {
      return;
    }

    const handleResize = () => {
      onRecheckScrollState();
    };

    globalThis.window.addEventListener("resize", handleResize);

    return () => {
      globalThis.window.removeEventListener("resize", handleResize);
    };
  }, [onRecheckScrollState]);

  const scrollToNext = useCallback(() => {
    if (!canScrollRight) {
      return;
    }
    scrollByViewport({ direction: "next", listRef });
  }, [canScrollRight, listRef]);

  const scrollToPrevious = useCallback(() => {
    if (!canScrollLeft) {
      return;
    }
    scrollByViewport({ direction: "previous", listRef });
  }, [canScrollLeft, listRef]);

  if (!isWeb() || isListEmpty) {
    return null;
  }

  return (
    <>
      {canScrollLeft && (
        <FloatingIconContainer side="left">
          <IconButton
            iconName={FAIconName.ChevronLeft}
            onPress={scrollToPrevious}
            testID={joinTestIDs(testID, "scroll-left")}
            variant="primarySolid"
          />
        </FloatingIconContainer>
      )}

      {canScrollRight && (
        <FloatingIconContainer side="right">
          <IconButton
            iconName={FAIconName.ChevronRight}
            onPress={scrollToNext}
            testID={joinTestIDs(testID, "scroll-right")}
            variant="primarySolid"
          />
        </FloatingIconContainer>
      )}
    </>
  );
};

export default HorizontalListNavigationButtons;
export { HorizontalListScrollConfig };
export type { HorizontalListNavigationButtonsProps };
